import * as React from "react";
const { useState, useEffect } = React;
import { compose } from 'recompose';
import { withStyles, Button, Grid } from '@material-ui/core';
import { getDataWithQueryString } from '../../../../../api/componentAPIs';
import WithModal from "../Pickups/PickupManagement/components/WithModal";
import TrailerStopGrid from "./TrailerStopGrid";
import TrailerFields from "./TrailerFields";
import appConstants from "@appConstants";
import { getSpecialNeedsOptionsWithServiceLevel } from "../../../utils";

interface Trailer {
  routeId?: string | number;
  trailerNumber?: string;
  trailerStatus?: string;
  manifestNumber?: string;
  routeName?: string;
  plannedRouteStartTime?: string;
  trailerType?: string;
}

interface StopData {
  stopId: string | number;
  [key: string]: unknown;
}

// SpecialNeedsRenderer's COD tooltip reads isCodCharge/isFreightCharge (not isCodShipment, which only
// decides whether the icon shows) — derive them locally since GetClosedTrailerContents doesn't send them.
const mapCodFields = (stop: StopData): StopData => ({
  ...stop,
  isCodCharge: stop.isCodShipment,
  isFreightCharge: stop.isFreightShipment,
});

const styles: any = (theme: any) => ({
  addProContainer: {
    height: '100%'
  },
  formContainer: {
    padding: 16,
    height: '86%',
    overflowY: 'auto'
  },
  actionButton: {
    textTransform: 'none',
    minWidth: 120,
    margin: '0px 4px'
  },
  actionContainer: {
    display: 'flex',
    paddingTop: 20,
    paddingRight: 20,
    justifyContent: 'flex-end'
  },
  trailerContentsContainer: {
    marginTop: 12,
    height: '90%',
    border: `1px solid ${theme.AppBGColor}`
  },
  trailerFieldsContainer: {
    height: '100%'
  }
});

interface TrailerContentsProps {
  classes: Record<string, string>;
  handleClose: () => void;
  trailer: Trailer | null;
  terminalCode: string;
  priorityShipmentList?: string[];
}

const TrailerContents = ({ classes, handleClose, trailer, terminalCode, priorityShipmentList }: TrailerContentsProps) => {
  const [trailerContents, setTrailerContents] = useState<StopData[] | null>(null);
  const [gridStatus, setGridStatus] = useState(appConstants.GRID_STATUS.LOADING);
  const specialNeedsOptions = getSpecialNeedsOptionsWithServiceLevel(priorityShipmentList ?? []);

  useEffect(() => {
    getTrailerContents();
  }, []);

  const getTrailerContents = () => {
    if (!trailer?.routeId || !terminalCode) {
      setGridStatus(appConstants.GRID_STATUS.SUCCESS);
      setTrailerContents([]);
      return;
    }
    setGridStatus(appConstants.GRID_STATUS.LOADING);
    getDataWithQueryString("GetClosedTrailerContents", `terminalCode=${terminalCode}&routeId=${trailer.routeId}`)
      .then((response) => {
        setGridStatus(appConstants.GRID_STATUS.SUCCESS);
        const sortedData = response.data?.sort((a, b) => a.plannedStopSequence - b.plannedStopSequence);
        setTrailerContents((sortedData || []).map(mapCodFields));
      })
      .catch((error: { message: string }) => {
        setTrailerContents([]);
        setGridStatus(appConstants.GRID_STATUS.ERROR);
      });
  };
  return (
    <div className={classes.addProContainer}>
      <div className={`${classes.formContainer} customScroll`}>
        <Grid container item xs={12} md={12} className={classes.trailerFieldsContainer}>
          <TrailerFields label="Trailer Number" value={trailer?.trailerNumber} isSelectedValue />
          <TrailerFields label="Trailer Status" value={trailer?.trailerStatus} isSelectedValue />
          <TrailerFields label="Manifest Number" value={trailer?.manifestNumber} />
          <TrailerFields label="Route" value={trailer?.routeName} />
          <TrailerFields label="Route Start Time" value={trailer?.plannedRouteStartTime} />
          <TrailerFields label="Trailer Type" value={trailer?.trailerType} />
          <Grid item xs={12} md={12} className={classes.trailerContentsContainer}>
            <TrailerStopGrid stopData={trailerContents} gridStatus={gridStatus} specialNeedsOptions={specialNeedsOptions} />
          </Grid>
        </Grid>
      </div>
      <div className={classes.actionContainer}>
        <Button
          variant="outlined"
          color="primary"
          size="medium"
          onClick={handleClose}
          classes={{ root: classes.actionButton }}
          id="closeButton">
          Close
        </Button>
      </div>
    </div >
  );
};

export default compose(withStyles(styles), WithModal)(TrailerContents as React.ComponentType<any>);
